import {ChangeDetectionStrategy, Component, ViewEncapsulation, Input, OnDestroy} from '@angular/core';
import {BaseComponent} from "../../shared/base.component";
import {ConfigStub} from "../../_typing/config-stub";
import {Series} from "../../_models/series";
import {getBaseFilter, getListFilter} from "../../services/api/filter_utils";
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {EventConfigColumn} from "../../_typing/config/config-column";
import {BeforeSaveAction} from "../../_typing/utils/before-save";
import {
    SeriesAdjustmentConfig,
    SeriesAdjustmentConfigAdjustment,
    seriesAdjustmentTypeArray
} from "../../_models/jobs/series-correction-job";
import { SeriesDataService } from "../../services/series_data.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {
    SelectSearchComponentComponent
} from "../../components/select-search-api/single/select-search-component.component";
import {SnakeCaseToTitleCasePipe} from "../../shared/pipes";
import {CommonModule} from "@angular/common";

// Extend SeriesAdjustmentConfigAdjustment interface locally to add the dynamic filter property
interface AdjustmentWithFilter extends SeriesAdjustmentConfigAdjustment {
    dynamicAdjustFilters?: any[] | null;
    loadingAdjustFilter?: boolean; // Optional: for loading state
}

@Component({
    selector: 'series-adjustment-form',
    templateUrl: './series-adjustment-form.component.html',
    styleUrls: ['./series-adjustment-form.component.scss'],
    imports: [
        COMMON_FORM_IMPORTS,
        CommonModule,
        CdkDropList,
        CdkDrag,
        SelectSearchComponentComponent,
        SnakeCaseToTitleCasePipe,
    ],
    encapsulation: ViewEncapsulation.None
})
export class SeriesAdjustmentFormComponent extends BaseComponent implements BeforeSaveAction, OnDestroy {
    @Input() config: SeriesAdjustmentConfig & { adjustments: AdjustmentWithFilter[] }; // Use the extended interface
    targetFilters = [getBaseFilter(true, 'is_calculation', 'eq')];
    baseAdjustFilters = [
        {
            or: [getBaseFilter(false, 'is_calculation', 'eq'),
                getBaseFilter(null, 'is_calculation', 'eq')]
        }
    ];
    //Constant should be readded to this array once it is supported
    seriesAdjustmentTypeArray = seriesAdjustmentTypeArray;
    error = '';
    private destroy$ = new Subject<void>();

    constructor(private seriesDataService: SeriesDataService) { // Inject SeriesDataService
        super();
    }

    ngOnInit() {
        if (!this.config.adjustments?.length) {
            this.addAdjustment();
        }
        // Initialize dynamic filters for existing adjustments if any
        this.config.adjustments.forEach(adj => {
            // Explicitly treat adj as AdjustmentWithFilter
            (adj as AdjustmentWithFilter).dynamicAdjustFilters = null;
            (adj as AdjustmentWithFilter).loadingAdjustFilter = false;
        });
    }

    addAdjustment() {
        // Push an object conforming to AdjustmentWithFilter
        this.config.adjustments.push({
            ...new SeriesAdjustmentConfigAdjustment(),
            dynamicAdjustFilters: null,
            loadingAdjustFilter: false
        });
    }

    removeAdjustment(index) {
        this.config.adjustments.splice(index, 1);
    }

    targetSelected(event, configRow: AdjustmentWithFilter) {
        const selectedTargetSeries: Series | null = event.value;
        configRow.target_series = selectedTargetSeries ? new ConfigStub(selectedTargetSeries) : null;

        // Reset adjust series and dynamic filter when target changes
        configRow.adjust = null;
        configRow.dynamicAdjustFilters = null;
        configRow.loadingAdjustFilter = false; // Reset loading state

        if (selectedTargetSeries) {
            configRow.loadingAdjustFilter = true; // Set loading state
            this.seriesDataService.getCalculationTreeNodeMap(selectedTargetSeries)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (treeMap) => {
                        const seriesIdsInTree = Object.keys(treeMap);
                        if (seriesIdsInTree.length > 0) {
                            // Filter to include only non-calculation series within the tree
                            configRow.dynamicAdjustFilters = [
                                getListFilter('id', seriesIdsInTree),
                                this.baseAdjustFilters[0] // Add the non-calculation filter
                            ];
                        } else {
                            // Handle case where tree is empty (shouldn't happen for a calculation?)
                            configRow.dynamicAdjustFilters = []; // Or some other indicator of no valid options
                        }
                        configRow.loadingAdjustFilter = false; // Clear loading state
                    },
                    error: (err) => {
                        console.error("Error fetching calculation tree:", err);
                        this.error = "Error fetching dependency tree for adjustment series.";
                        configRow.dynamicAdjustFilters = []; // Prevent selection on error
                        configRow.loadingAdjustFilter = false; // Clear loading state
                    }
                });
        }
    }

    adjustSelected(event, configRow: AdjustmentWithFilter, i) {
        if (!this.validateAdjustment(event.value, 'adjust', i)) { return; }
        configRow.adjust = event.value;
    }

    validateAdjustment(value: Series, key: 'adjust' | 'target_series', index: number): boolean {
        this.error = "";
        const adjustsSelected = this.config.adjustments.map(a => a[key]?.id);
        if (adjustsSelected.includes(value.id) && adjustsSelected[index] !== value.id) {
            this.error = "ERROR: Duplicate series selected.";
            return false;
        }
        return true;
    }

    beforeSave(): boolean {
        if (this.error) {
            return false;
        }
        let valid = true;
        this.config.adjustments.forEach(a => {
            if (!a.target_series || !a.adjust) {
                this.error = "ERROR: Some adjustment rows are incomplete.";
                valid = false;
            }
        });
        return valid;
    }

    drop(event: CdkDragDrop<AdjustmentWithFilter[]>): void {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
